/* ================================================================
   Weinkarte als PDF — ohne Umweg über den Druckdialog.

   Der bisherige Weg über window.print() erzeugte in Chromium aus dem
   Dialog heraus fehlerhafte PDFs (dieselbe Seite wiederholte sich,
   weil der Kartenbereich in einem fixierten Vorfahren lag). Hier
   entsteht die Datei stattdessen direkt: ein kleiner, abhängigkeits-
   freier PDF-Writer mit den Standardschriften (Helvetica, WinAnsi).

   Gliederung ist immer Land → Region → Winzer → Wein; je Wein stehen
   die Jahrgänge mit Größe, Menge und Preis in eigenen Zeilen. Auf
   Wunsch kommen ein Inhaltsverzeichnis (Länder, Regionen) mit echten
   Seitenzahlen und eine Seitennummerierung dazu.
   ================================================================ */

import { FLASCHENGROESSEN, NV, TYPEN } from "../kern/konstanten.js";
import { karteAufbereiten } from "../kern/weinkarte.js";
import { isoVon } from "../i18n/laender.js";

/* — Seitengeometrie (A4, Punkte) ——————————————————————————————— */
const SEITE_B = 595.28;
const SEITE_H = 841.89;
const RAND = 56;
const OBEN = SEITE_H - 64;
const UNTEN = 64;
const BREITE = SEITE_B - 2 * RAND;

/* — Beschriftungen in der Sprache der Karte ———————————————————— */
const TEXTE = {
  de: { inhalt: "Inhalt", seite: "Seite {n} von {m}", fl: "Fl.", nv: "N.V.", weinkarte: "Weinkarte", ohneLabel: "Weitere Weine" },
  en: { inhalt: "Contents", seite: "Page {n} of {m}", fl: "btl.", nv: "N.V.", weinkarte: "Wine list", ohneLabel: "More wines" },
  es: { inhalt: "Índice", seite: "Página {n} de {m}", fl: "bot.", nv: "N.V.", weinkarte: "Carta de vinos", ohneLabel: "Más vinos" },
  fr: { inhalt: "Sommaire", seite: "Page {n} sur {m}", fl: "bout.", nv: "N.V.", weinkarte: "Carte des vins", ohneLabel: "Autres vins" },
};
const LOCALES = { de: "de-DE", en: "en-GB", es: "es-ES", fr: "fr-FR" };

/* — Schriftmetriken (AFM-Breiten, 1/1000 em, WinAnsi) —————————— */
/* Helvetica, Zeichen 32–126. */
const HELV = [278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,
  556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,1015,
  667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,667,778,722,667,611,722,667,944,667,667,611,
  278,278,278,469,556,333,
  556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,556,556,333,500,278,556,500,722,500,500,500,
  334,260,334,584];
/* Helvetica-Bold, Zeichen 32–126. */
const HELVB = [278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,
  556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,975,
  722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,667,778,722,667,611,722,667,944,667,667,611,
  333,278,333,584,556,333,
  556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,611,611,389,556,333,611,556,778,556,556,500,
  389,280,389,584];
/* Diakritika teilen die Breite ihres Grundbuchstabens; wenige
   Sonderzeichen bekommen feste Werte. */
const EXTRA = {
  "\u00e4": "a", "\u00f6": "o", "\u00fc": "u", "\u00c4": "A", "\u00d6": "O", "\u00dc": "U",
  "\u00e0": "a", "\u00e1": "a", "\u00e2": "a", "\u00e8": "e", "\u00e9": "e", "\u00ea": "e",
  "\u00ee": "i", "\u00ef": "i", "\u00f4": "o", "\u00f9": "u", "\u00fb": "u", "\u00e7": "c",
  "\u00f1": "n", "\u00c9": "E", "\u00c8": "E", "\u00c1": "A", "\u00cd": "I", "\u00d3": "O", "\u00da": "U",
};
const EXTRA_FEST = {
  "\u00df": 611, "\u20ac": 556, "\u00b0": 400, "\u00b7": 278,
  "\u2013": 556, "\u2014": 1000, "\u2018": 222, "\u2019": 222,
  "\u201c": 333, "\u201d": 333, "\u201e": 333, "\u2026": 1000,
};

function zBreite(zeichen, fett) {
  const basis = EXTRA[zeichen] || zeichen;
  const code = basis.charCodeAt(0);
  if (code >= 32 && code <= 126) return (fett ? HELVB : HELV)[code - 32];
  if (EXTRA_FEST[zeichen] != null) return EXTRA_FEST[zeichen];
  return 556; /* unbekannt: Durchschnittsbreite */
}

function textBreite(text, groesse, fett = false) {
  let s = 0;
  for (const z of String(text)) s += zBreite(z, fett);
  return (s / 1000) * groesse;
}

/* Greedy-Umbruch an Leerzeichen; überlange Wörter werden hart geteilt. */
function umbrechen(text, groesse, fett, maxBreite) {
  const zeilen = [];
  for (const abs of String(text).split("\n")) {
    let zeile = "";
    for (const wort of abs.split(/\s+/).filter(Boolean)) {
      const probe = zeile ? zeile + " " + wort : wort;
      if (textBreite(probe, groesse, fett) <= maxBreite) { zeile = probe; continue; }
      if (zeile) zeilen.push(zeile);
      if (textBreite(wort, groesse, fett) <= maxBreite) { zeile = wort; continue; }
      let rest = wort;
      while (textBreite(rest, groesse, fett) > maxBreite) {
        let i = 1;
        while (i < rest.length && textBreite(rest.slice(0, i + 1), groesse, fett) <= maxBreite) i++;
        zeilen.push(rest.slice(0, i));
        rest = rest.slice(i);
      }
      zeile = rest;
    }
    zeilen.push(zeile);
  }
  return zeilen.length ? zeilen : [""];
}

/* — WinAnsi-Kodierung für PDF-Strings —————————————————————————— */
const WINANSI = {
  "\u20ac": 0x80, "\u201a": 0x82, "\u201e": 0x84, "\u2026": 0x85, "\u2020": 0x86, "\u2021": 0x87,
  "\u2030": 0x89, "\u2018": 0x91, "\u2019": 0x92, "\u201c": 0x93, "\u201d": 0x94, "\u2022": 0x95,
  "\u2013": 0x96, "\u2014": 0x97, "\u02dc": 0x98, "\u0161": 0x9a, "\u0153": 0x9c, "\u017e": 0x9e,
  "\u0178": 0x9f, "\u0160": 0x8a, "\u0152": 0x8c, "\u017d": 0x8e,
};
function pdfString(text) {
  let out = "";
  for (const z of String(text)) {
    let code = z.charCodeAt(0);
    if (code > 0xff) code = WINANSI[z] != null ? WINANSI[z] : 0x3f; /* ? */
    const c = String.fromCharCode(code);
    if (c === "(" || c === ")" || c === "\\") out += "\\" + c;
    else if (code < 32) out += "\\" + code.toString(8).padStart(3, "0");
    else out += c;
  }
  return out;
}

/* Ländername in der Sprache der Karte — unabhängig davon, welche
   Sprache die Oberfläche gerade zeigt. Unbekanntes bleibt stehen. */
function landInSprache(name, code) {
  const iso = isoVon(name);
  if (!iso) return name || "";
  try {
    return new Intl.DisplayNames([LOCALES[code] || "de-DE"], { type: "region" }).of(iso) || name;
  } catch (e) { return name || ""; }
}

/* — Formatierung ——————————————————————————————————————————————— */
const jahrText = (jahr, tx) => (jahr === NV || jahr == null ? tx.nv : String(jahr));

function euro(n, code) {
  if (n == null) return "";
  try {
    return n.toLocaleString(LOCALES[code] || "de-DE", {
      style: "currency", currency: "EUR",
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    });
  } catch (e) { return String(n) + " \u20ac"; }
}

const groesseKurz = (g) => {
  if (!g) return "";
  const fg = FLASCHENGROESSEN.find((x) => String(x.key) === String(g));
  return fg ? fg.kurz : String(g);
};

/* — Gliederung —————————————————————————————————————————————————
   Zwei Anordnungen stehen zur Wahl. Die Herkunft ist die Vorgabe;
   wer seine Weine mit eigenen Labels ordnet (etwa „New Wave
   Chardonnay“), stellt das Label voran. Innerhalb bleibt es bei der
   gewohnten Folge. */
const EBENEN = {
  herkunft: ["land", "region", "winzer"],
  label: ["label", "land", "region", "winzer"],
};
/* Sortier- und Anzeigefeld je Ebene. Sortiert wird über die echten
   Daten, angezeigt nur, was freigegeben ist. */
const SORTFELD = { land: "sortLand", region: "sortRegion", winzer: "sortWinzer", label: "sortLabel" };
const NAMENSFELD = { land: "land", region: "region", winzer: "winzer", label: "label" };

const ENDE = "\uffff";
function gliedern(eintraege, modus = "herkunft") {
  const ebenen = EBENEN[modus] || EBENEN.herkunft;
  const sortiert = [...eintraege].sort((a, b) => {
    for (const art of ebenen) {
      const f = SORTFELD[art];
      const c = (a[f] || ENDE).localeCompare(b[f] || ENDE, "de");
      if (c) return c;
    }
    return String(a.name).localeCompare(String(b.name), "de") || (b.jahr || 0) - (a.jahr || 0);
  });

  const wurzel = { kinder: [] };
  for (const e of sortiert) {
    let knoten = wurzel;
    for (const art of ebenen) {
      const schl = e[SORTFELD[art]] || "";
      let letzter = knoten.kinder[knoten.kinder.length - 1];
      if (!letzter || letzter.art !== art || letzter.schl !== schl) {
        /* Der Anzeigename kommt aus dem freigegebenen Feld; fehlt er,
           bleibt die Überschrift leer und wird später übersprungen. */
        letzter = { art, schl, name: e[NAMENSFELD[art]] || "", kinder: [], weine: [] };
        knoten.kinder.push(letzter);
      }
      knoten = letzter;
    }
    let wein = knoten.weine[knoten.weine.length - 1];
    if (!wein || wein.name !== e.name) {
      wein = { name: e.name, typ: e.typ || null, rebsorten: e.rebsorten || [],
        label: e.label || "", posten: [] };
      knoten.weine.push(wein);
    }
    wein.posten.push(e);
  }
  return wurzel.kinder;
}

/* — Seitenaufbau ——————————————————————————————————————————————— */
const GRAU = "0.42 0.42 0.42";
const AKZENT = "0.45 0.18 0.22"; /* Granatton der Anwendung */

/* Überschriften nach Rang, nicht nach Art: so trägt die oberste
   gesetzte Ebene immer dieselbe Handschrift — gleich, ob dort Land
   oder Label steht. `platz` hält die Überschrift bei ihrem Inhalt. */
const KOPFSTIL = [
  { vor: 26, platz: 70, groesse: 13, farbe: AKZENT, gross: true, linie: true },
  { vor: 20, platz: 56, groesse: 11.5, farbe: "0 0 0" },
  { vor: 16, platz: 44, groesse: 10.5, farbe: GRAU },
  { vor: 14, platz: 40, groesse: 10, farbe: GRAU },
];

function neueSeite(dok) {
  dok.seiten.push({ befehle: [] });
  dok.y = OBEN;
}
function seite(dok) { return dok.seiten[dok.seiten.length - 1]; }

function platz(dok, hoehe) {
  if (dok.y - hoehe < UNTEN) neueSeite(dok);
}

function schreibe(dok, text, { x = RAND, groesse = 10, fett = false, kursiv = false, farbe = "0 0 0", rechts = null } = {}) {
  const font = fett ? "/F2" : kursiv ? "/F3" : "/F1";
  const px = rechts != null ? rechts - textBreite(text, groesse, fett) : x;
  seite(dok).befehle.push(
    `BT ${font} ${groesse} Tf ${farbe} rg 1 0 0 1 ${px.toFixed(2)} ${dok.y.toFixed(2)} Tm (${pdfString(text)}) Tj ET`);
}
function linie(dok, y, staerke = 0.75, farbe = AKZENT) {
  seite(dok).befehle.push(`${farbe} RG ${staerke} w ${RAND} ${y.toFixed(2)} m ${(SEITE_B - RAND).toFixed(2)} ${y.toFixed(2)} l S`);
}
function absatz(dok, text, opts, zeilenhoehe, maxBreite = BREITE, einzug = RAND) {
  for (const z of umbrechen(text, opts.groesse, !!opts.fett, maxBreite)) {
    platz(dok, zeilenhoehe);
    dok.y -= zeilenhoehe;
    schreibe(dok, z, { ...opts, x: einzug });
  }
}

/* Höhe eines Weinblocks vorab schätzen, damit er möglichst nicht
   mitten in einer Position umbricht. */
function weinBlockHoehe(wein, felder, tx, mitLabel = false) {
  let h = 16; /* Namenszeile */
  if (wein.rebsorten.length || (mitLabel && wein.label)) h += 12;
  for (const p of wein.posten) {
    h += 14;
    if ([p.alkohol, p.restzucker, p.saeure].some((v) => v != null)) h += 11;
    if (p.notiz) h += 11 * Math.max(1, Math.ceil(p.notiz.length / 90));
  }
  return h + 6;
}

function analytikText(p) {
  return [
    p.alkohol != null ? `${String(p.alkohol).replace(".", ",")} % vol` : null,
    p.saeure != null ? `${String(p.saeure).replace(".", ",")} g/l S\u00e4ure` : null,
    p.restzucker != null ? `${String(p.restzucker).replace(".", ",")} g/l Restzucker` : null,
  ].filter(Boolean).join(" \u00b7 ");
}

function weinBlock(dok, wein, felder, tx, code, mitLabel = false) {
  platz(dok, 16);
  dok.y -= 16;
  let name = wein.name;
  if (felder.typ && wein.typ && TYPEN[wein.typ]) name += ` \u00b7 ${TYPEN[wein.typ].label}`;
  schreibe(dok, name, { groesse: 10.5, fett: true });
  /* Rebsorten und — sofern das Label nicht ohnehin die Überschrift
     stellt — das eigene Label in einer grauen Zeile darunter. */
  const nebenzeile = [
    wein.rebsorten.length ? wein.rebsorten.join(" \u00b7 ") : null,
    mitLabel && wein.label ? wein.label : null,
  ].filter(Boolean).join("  \u2014  ");
  if (nebenzeile) {
    dok.y -= 12;
    schreibe(dok, nebenzeile, { groesse: 8.5, farbe: GRAU });
  }
  for (const p of wein.posten) {
    platz(dok, 14);
    dok.y -= 14;
    const links = [
      p.jahr != null ? jahrText(p.jahr, tx) : null,
      groesseKurz(p.groesse) || null,
      p.bestand != null ? `${p.bestand} ${tx.fl}` : null,
    ].filter(Boolean).join(" \u00b7 ") || "\u2014";
    schreibe(dok, links, { x: RAND + 14, groesse: 9.5 });
    if (p.preis != null) {
      schreibe(dok, euro(p.preis, code), { groesse: 10, fett: true, rechts: SEITE_B - RAND });
    }
    const ana = analytikText(p);
    if (ana) {
      dok.y -= 11;
      schreibe(dok, ana, { x: RAND + 14, groesse: 8.5, farbe: GRAU });
    }
    if (p.notiz) {
      absatz(dok, p.notiz, { groesse: 8.5, kursiv: true, farbe: GRAU }, 11, BREITE - 14, RAND + 14);
    }
  }
  dok.y -= 6;
}

/* — Hauptaufbau ———————————————————————————————————————————————— */
function erzeugeWeinkartePdf(db, karte) {
  const code = ["de", "en", "es", "fr"].includes(karte.sprache) ? karte.sprache : "de";
  const tx = TEXTE[code];
  const daten = karteAufbereiten({ weine: db.weine, winzer: db.winzer, regions: db.regions }, karte);
  const felder = daten.felder;
  const modus = karte.pdfGliederung === "label" ? "label" : "herkunft";
  /* Ländernamen in der Sprache der Karte — sie bestimmen zugleich die
     Reihenfolge, damit eine englische Karte nicht in deutscher
     Sortierung erscheint. */
  const eintraege = daten.eintraege.map((e) => {
    const land = landInSprache(e.land, code);
    return { ...e, land, sortLand: landInSprache(e.sortLand, code) || land };
  });
  const knoten = gliedern(eintraege, modus);
  const mitSeiten = !!karte.pdfSeiten;
  const titel = daten.titel || tx.weinkarte;

  /* Wird die Karte nach Label gegliedert, sind die Labels selbst die
     Überschriften — unabhängig davon, ob sie zusätzlich als Angabe am
     Wein freigegeben sind. Sonst richten sich die Überschriften nach
     den sichtbaren Angaben. */
  const zeigtEbene = (art) => art === "label" ? modus === "label" : !!felder[art];
  const zeigtLabelAmWein = !!felder.label && modus !== "label";
  /* Tragen im Labelmodus einige Weine kein Label, bekommen sie am
     Ende eine eigene, benannte Gruppe statt einer stummen. */
  const hatLabelNamen = modus === "label" && knoten.some((k) => k.schl);
  /* Überschrift einer Ebene. Das Label ist die vom Nutzer gewählte
     Ordnung seiner eigenen Karte — es steht deshalb auch dann über
     der Gruppe, wenn es als Angabe am Wein verborgen bleibt. */
  const kopfName = (k) => k.art === "label"
    ? (k.schl || (hatLabelNamen ? tx.ohneLabel : ""))
    : k.name;

  /* Ins Verzeichnis kommen die beiden obersten gesetzten Ebenen —
     also je nach Gliederung Land/Region oder Label/Land. */
  const TOC_ARTEN = new Set(["label", "land", "region"]);
  const imVerzeichnis = (k, rang) => rang < 2 && TOC_ARTEN.has(k.art);

  const zaehleVerzeichnis = (liste, rang) => {
    let n = 0;
    for (const k of liste) {
      const zeigt = zeigtEbene(k.art) && !!kopfName(k);
      if (zeigt && imVerzeichnis(k, rang)) n++;
      if (k.kinder.length) n += zaehleVerzeichnis(k.kinder, zeigt ? rang + 1 : rang);
    }
    return n;
  };
  const inhaltZeilen = karte.pdfInhalt ? zaehleVerzeichnis(knoten, 0) : 0;
  const mitInhalt = !!karte.pdfInhalt && inhaltZeilen > 0;
  const titelHoehe = 30 + 14 + 18; /* Titel + Linie + Abstand */
  let inhaltSeiten = 0;
  if (mitInhalt && inhaltZeilen > 0) {
    const ersteSeite = Math.floor((OBEN - UNTEN - titelHoehe - 26) / 16);
    const proSeite = Math.floor((OBEN - UNTEN) / 16);
    inhaltSeiten = 1 + Math.max(0, Math.ceil((inhaltZeilen - ersteSeite) / proSeite));
  }

  /* Erster Durchgang: Inhalt setzen, Seitenzahlen je Land/Region
     merken. Die ersten `inhaltSeiten` Seiten bleiben dem Verzeichnis
     vorbehalten und werden anschließend gefüllt. */
  const dok = { seiten: [], y: OBEN };
  for (let i = 0; i < inhaltSeiten; i++) neueSeite(dok);
  neueSeite(dok);

  if (!mitInhalt || inhaltSeiten === 0) {
    /* Titel direkt über dem Inhalt */
    dok.y -= 24;
    schreibe(dok, titel, { groesse: 20, fett: true });
    dok.y -= 12;
    linie(dok, dok.y);
    dok.y -= 10;
  }

  const marken = []; /* { rang, name, seite } — Seiten sind 1-basiert */
  const zeichneEbene = (liste, rang) => {
    for (const k of liste) {
      const name = kopfName(k);
      const zeigt = zeigtEbene(k.art) && !!name;
      if (zeigt) {
        const stil = KOPFSTIL[Math.min(rang, KOPFSTIL.length - 1)];
        platz(dok, stil.platz); /* Überschrift nicht als Waise ans Seitenende */
        dok.y -= stil.vor;
        schreibe(dok, stil.gross ? name.toUpperCase() : name,
          { groesse: stil.groesse, fett: true, farbe: stil.farbe });
        if (stil.linie) { dok.y -= 6; linie(dok, dok.y, 0.75); dok.y -= 4; }
        if (imVerzeichnis(k, rang)) marken.push({ rang, name, seite: dok.seiten.length });
      }
      const unterRang = zeigt ? rang + 1 : rang;
      if (k.kinder.length) zeichneEbene(k.kinder, unterRang);
      for (const wein of k.weine) {
        const h = weinBlockHoehe(wein, felder, tx, zeigtLabelAmWein);
        if (dok.y - h < UNTEN && h < OBEN - UNTEN) neueSeite(dok);
        weinBlock(dok, wein, felder, tx, code, zeigtLabelAmWein);
      }
    }
  };
  zeichneEbene(knoten, 0);

  /* Zweiter Schritt: Verzeichnis in die reservierten Seiten. */
  if (mitInhalt && inhaltSeiten > 0) {
    const inhaltDok = { seiten: dok.seiten.slice(0, inhaltSeiten), y: OBEN };
    let idx = 0;
    const ziel = () => inhaltDok.seiten[Math.min(idx, inhaltSeiten - 1)];
    const setze = (text, opts) => {
      const px = opts.rechts != null ? opts.rechts - textBreite(text, opts.groesse, !!opts.fett) : (opts.x != null ? opts.x : RAND);
      const font = opts.fett ? "/F2" : opts.kursiv ? "/F3" : "/F1";
      ziel().befehle.push(`BT ${font} ${opts.groesse} Tf ${opts.farbe || "0 0 0"} rg 1 0 0 1 ${px.toFixed(2)} ${inhaltDok.y.toFixed(2)} Tm (${pdfString(text)}) Tj ET`);
    };
    inhaltDok.y -= 24;
    setze(titel, { groesse: 20, fett: true });
    inhaltDok.y -= 12;
    ziel().befehle.push(`${AKZENT} RG 0.75 w ${RAND} ${inhaltDok.y.toFixed(2)} m ${(SEITE_B - RAND).toFixed(2)} ${inhaltDok.y.toFixed(2)} l S`);
    inhaltDok.y -= 26;
    setze(tx.inhalt, { groesse: 13, fett: true });
    for (const m of marken) {
      /* Zeile passt nicht mehr — auf die nächste Verzeichnisseite. */
      if (inhaltDok.y - 16 < UNTEN) { idx++; inhaltDok.y = OBEN; }
      inhaltDok.y -= 16;
      const einzug = m.rang === 0 ? RAND : RAND + 16;
      setze(m.name, { x: einzug, groesse: m.rang === 0 ? 10.5 : 9.5, fett: m.rang === 0 });
      /* Punktreihe bis zur rechtsbündigen Seitenzahl */
      const nummer = String(m.seite);
      const nB = textBreite(nummer, 10);
      const tB = textBreite(m.name, m.rang === 0 ? 10.5 : 9.5, m.rang === 0);
      const von = einzug + tB + 6, bis = SEITE_B - RAND - nB - 6;
      if (bis > von + 10) {
        const punkte = ".".repeat(Math.max(0, Math.floor((bis - von) / textBreite(".", 9))));
        ziel().befehle.push(`BT /F1 9 Tf ${GRAU} rg 1 0 0 1 ${von.toFixed(2)} ${inhaltDok.y.toFixed(2)} Tm (${punkte}) Tj ET`);
      }
      setze(nummer, { groesse: 10, rechts: SEITE_B - RAND });
    }
  }

  /* Fußzeile mit Seitenzahlen — erst jetzt steht die Gesamtzahl fest. */
  if (mitSeiten) {
    const m = dok.seiten.length;
    dok.seiten.forEach((sz, i) => {
      const text = tx.seite.replace("{n}", String(i + 1)).replace("{m}", String(m));
      const b = textBreite(text, 9);
      sz.befehle.push(`BT /F1 9 Tf ${GRAU} rg 1 0 0 1 ${((SEITE_B - b) / 2).toFixed(2)} 34 Tm (${pdfString(text)}) Tj ET`);
    });
  }

  return serialisieren(dok.seiten);
}

/* — PDF-Serialisierung ————————————————————————————————————————— */
function serialisieren(seiten) {
  const objekte = []; /* 1-basiert: objekte[i] = Körper von Objekt i+1 */
  const nSeiten = seiten.length;
  const kids = seiten.map((_, i) => `${6 + i * 2} 0 R`).join(" ");
  objekte.push("<< /Type /Catalog /Pages 2 0 R >>");
  objekte.push(`<< /Type /Pages /Kids [${kids}] /Count ${nSeiten} >>`);
  objekte.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>");
  objekte.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>");
  objekte.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique /Encoding /WinAnsiEncoding >>");
  for (let i = 0; i < nSeiten; i++) {
    const inhalt = seiten[i].befehle.join("\n");
    objekte.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${SEITE_B} ${SEITE_H}] `
      + `/Resources << /Font << /F1 3 0 R /F2 4 0 R /F3 5 0 R >> >> /Contents ${7 + i * 2} 0 R >>`);
    objekte.push(`<< /Length ${inhalt.length} >>\nstream\n${inhalt}\nendstream`);
  }

  let pdf = "%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n";
  const versatz = [];
  objekte.forEach((koerper, i) => {
    versatz.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${koerper}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objekte.length + 1}\n0000000000 65535 f \n`;
  for (const v of versatz) pdf += `${String(v).padStart(10, "0")} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objekte.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;

  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff;
  return bytes;
}

/* — Browser: Datei anbieten ———————————————————————————————————— */
function weinkartePdfLaden(db, karte) {
  const bytes = erzeugeWeinkartePdf(db, karte);
  const name = (karte.titel || "weinkarte").toLowerCase()
    .replace(/[\u00e4]/g, "ae").replace(/[\u00f6]/g, "oe").replace(/[\u00fc]/g, "ue").replace(/[\u00df]/g, "ss")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "weinkarte";
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name + ".pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export { erzeugeWeinkartePdf, weinkartePdfLaden, gliedern };
